import React, { useState, useEffect } from 'react';
import { Upload } from 'lucide-react';
import { apiCall } from '../../api/axios';
import toast from 'react-hot-toast';
import { useFileUpload } from '../../hooks/useBlobUrl';
import { currencyOptions } from '../../utils/currency';

const Organization = () => {
  const [org, setOrg] = useState({
    branchName: '',
    companyName: '',
    company: '',
    contactPerson: '',
    companyContact: '',
    contact: '',
    email: '',
    address: '',
    country: '',
    city: '',
    invoicePrefix: '',
    incorporationNo: '',
    vatId: '',
    vatPercentage: 0,
    logoUrl: '',
    poLogoUrl: '',
    invoiceLogoUrl: '',
    termsConditions: '',
    currency_code: 'AED'
  });
  const [loading, setLoading] = useState(false);
  const { selectedFile, previewUrl, handleFileSelect, clearFile } = useFileUpload();
  const [selectedPOFile, setSelectedPOFile] = useState(null);
  const [selectedInvoiceFile, setSelectedInvoiceFile] = useState(null);
  const [poPreviewUrl, setPOPreviewUrl] = useState('');
  const [invoicePreviewUrl, setInvoicePreviewUrl] = useState('');

  useEffect(() => {
    fetchOrganization();
  }, []);

  // Function to safely decode HTML entities using DOMParser
  const decodeHtmlEntities = (str) => {
    if (!str) return str;
    const parser = new DOMParser();
    const doc = parser.parseFromString(str, 'text/html');
    return doc.documentElement.textContent || str;
  };

  const fetchOrganization = async () => {
    try {
      const response = await apiCall.get('/api/organization');
      const data = response.data || {};
      
      // Decode HTML entities in logo URLs
      const logoUrl = decodeHtmlEntities(data.logoUrl || '');
      const poLogoUrl = decodeHtmlEntities(data.poLogoUrl || '');
      const invoiceLogoUrl = decodeHtmlEntities(data.invoiceLogoUrl || '');
      
      setOrg({
        branchName: data.branchName || '',
        companyName: data.companyName || data.company || '',
        company: data.company || '',
        contactPerson: data.contactPerson || '',
        companyContact: data.companyContact || data.contact || '',
        contact: data.contact || '',
        email: data.email || '',
        address: data.address || '',
        country: data.country || '',
        city: data.city || '',
        invoicePrefix: data.invoicePrefix || '',
        incorporationNo: data.incorporationNo || '',
        vatId: data.vatId || '',
        vatPercentage: data.vatPercentage || 0,
        logoUrl,
        poLogoUrl,
        invoiceLogoUrl,
        termsConditions: data.termsConditions || '',
        currency_code: data.currency_code || 'AED'
      });
    } catch (error) {
      toast.error(`Failed to fetch organization data: ${error.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let updatedOrg = { ...org };
      
      // Upload logos if selected
      if (selectedFile) {
        const formData = new FormData();
        formData.append('logo', selectedFile);
        
        const uploadResponse = await apiCall.post('/api/upload/logo', formData);
        
        updatedOrg.logoUrl = uploadResponse.data.url;
      }
      
      if (selectedPOFile) {
        const formData = new FormData();
        formData.append('poLogo', selectedPOFile);
        
        const uploadResponse = await apiCall.post('/api/upload/po-logo', formData);
        
        updatedOrg.poLogoUrl = uploadResponse.data.url;
      }
      
      if (selectedInvoiceFile) {
        const formData = new FormData();
        formData.append('invoiceLogo', selectedInvoiceFile);
        
        const uploadResponse = await apiCall.post('/api/upload/invoice-logo', formData);
        
        updatedOrg.invoiceLogoUrl = uploadResponse.data.url;
      }
      
      await apiCall.put('/api/organization', updatedOrg);
      setOrg(updatedOrg);
      clearFile();
      setSelectedPOFile(null);
      setSelectedInvoiceFile(null);
      setPOPreviewUrl('');
      setInvoicePreviewUrl('');
      toast.success('Organization settings updated');
    } catch (error) {
      toast.error('Failed to update organization settings');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };
  
  const handlePOLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedPOFile(file);
      setPOPreviewUrl(URL.createObjectURL(file));
    }
  };
  
  const handleInvoiceLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedInvoiceFile(file);
      setInvoicePreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setOrg(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="max-w-4xl">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Organization Settings</h2>

      <div className="bg-white rounded-lg shadow p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
              <input
                type="text"
                name="companyName"
                value={org.companyName || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Branch Name *</label>
              <input
                type="text"
                name="branchName"
                value={org.branchName || ''}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
              <input
                type="text"
                name="contactPerson"
                value={org.contactPerson || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Contact</label>
              <input
                type="text"
                name="companyContact"
                value={org.companyContact || ''}
                onChange={handleChange}
                placeholder="Phone number or contact info"
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={org.email || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Prefix</label>
              <input
                type="text"
                name="invoicePrefix"
                value={org.invoicePrefix || ''}
                onChange={handleChange}
                placeholder="e.g., INV-"
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
            <textarea
              name="address"
              value={org.address || ''}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <input
                type="text"
                name="country"
                value={org.country || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input
                type="text"
                name="city"
                value={org.city || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Tax Information */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Incorporation No.</label>
              <input
                type="text"
                name="incorporationNo"
                value={org.incorporationNo || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">VAT ID</label>
              <input
                type="text"
                name="vatId"
                value={org.vatId || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
              <select
                name="currency_code"
                value={org.currency_code || 'AED'}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {currencyOptions.map(currency => (
                  <option key={currency.code} value={currency.code}>
                    {currency.code} ({currency.symbol}) - {currency.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Logo Uploads */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Organization Logo</label>
              <div className="flex items-center space-x-4">
                {previewUrl ? (
                  <div className="w-16 h-16 border rounded-md overflow-hidden">
                    <img src={previewUrl} alt="Logo Preview" className="w-full h-full object-contain" />
                  </div>
                ) : org.logoUrl && (
                  <div className="w-16 h-16 border rounded-md overflow-hidden">
                    <img 
                      src={`${(window.ENV && window.ENV.REACT_APP_API_URL) || process.env.REACT_APP_API_URL || 'http://localhost:5000'}${org.logoUrl}`} 
                      alt="Logo" 
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        console.log('Logo load error:', e.target.src);
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className="w-full h-full flex items-center justify-center text-xs text-gray-500 hidden">
                      Logo not found
                    </div>
                  </div>
                )}
                <label className="cursor-pointer bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700">
                  <Upload className="w-4 h-4 inline mr-2" />
                  Upload Logo
                  <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                </label>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Purchase Order Logo</label>
              <div className="flex items-center space-x-4">
                {poPreviewUrl ? (
                  <div className="w-16 h-16 border rounded-md overflow-hidden">
                    <img src={poPreviewUrl} alt="PO Logo Preview" className="w-full h-full object-contain" />
                  </div>
                ) : org.poLogoUrl && (
                  <div className="w-16 h-16 border rounded-md overflow-hidden">
                    <img 
                      src={`${(window.ENV && window.ENV.REACT_APP_API_URL) || process.env.REACT_APP_API_URL || 'http://localhost:5000'}${org.poLogoUrl}`} 
                      alt="PO Logo" 
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        console.log('PO Logo load error:', e.target.src);
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className="w-full h-full flex items-center justify-center text-xs text-gray-500 hidden">
                      PO Logo not found
                    </div>
                  </div>
                )}
                <label className="cursor-pointer bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700">
                  <Upload className="w-4 h-4 inline mr-2" />
                  Upload PO Logo
                  <input type="file" accept="image/*" onChange={handlePOLogoUpload} className="hidden" />
                </label>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Logo</label>
              <div className="flex items-center space-x-4">
                {invoicePreviewUrl ? (
                  <div className="w-16 h-16 border rounded-md overflow-hidden">
                    <img src={invoicePreviewUrl} alt="Invoice Logo Preview" className="w-full h-full object-contain" />
                  </div>
                ) : org.invoiceLogoUrl && (
                  <div className="w-16 h-16 border rounded-md overflow-hidden">
                    <img 
                      src={`${(window.ENV && window.ENV.REACT_APP_API_URL) || process.env.REACT_APP_API_URL || 'http://localhost:5000'}${org.invoiceLogoUrl}`} 
                      alt="Invoice Logo" 
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        console.log('Invoice Logo load error:', e.target.src);
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className="w-full h-full flex items-center justify-center text-xs text-gray-500 hidden">
                      Invoice Logo not found
                    </div>
                  </div>
                )}
                <label className="cursor-pointer bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700">
                  <Upload className="w-4 h-4 inline mr-2" />
                  Upload Invoice Logo
                  <input type="file" accept="image/*" onChange={handleInvoiceLogoUpload} className="hidden" />
                </label>
              </div>
            </div>
          </div>

          {/* Terms & Conditions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Terms & Conditions</label>
            <textarea
              name="termsConditions"
              value={org.termsConditions || ''}
              onChange={handleChange}
              rows={6}
              placeholder="Enter your terms and conditions..."
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Organization;
